import React, { useEffect, useRef, useContext, useState } from "react";
import { Box, Paper, Stack, Avatar, CircularProgress, Typography, Button, IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogActions, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github-dark.css";
import { ChatContext } from "../context/chatcontext";
import { useAuth } from "../context/AuthContext";
import MessageActions from "./MessageActions";
import { EmptyChatState } from "./EmptyState";
import { toast } from "react-toastify";
import DownloadIcon from "@mui/icons-material/Download";
import { isProjectStructure, downloadProjectAsZip, extractProjectName } from "../utils/projectDownloadUtils";

export default function ChatWindow({ messages = [] }) {
  const chatEndRef = useRef(null);
  const { isProcessing, processingMessage, startNewChat, editMessage, addMessage, regenerateLastResponse } = useContext(ChatContext);
  const { user } = useAuth();
  const [hoveredMessage, setHoveredMessage] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState("");

  // Get user initial for avatar
  const getUserInitial = () => {
    if (!user) return "U";
    const name = user.name || user.email || "";
    return name.charAt(0).toUpperCase();
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isProcessing, processingMessage]);

  const handleCopyMessage = async (content) => {
    try {
      // Remove markdown image syntax for plain text copy
      const textToCopy = content.replace(/!\[.*?\]\(.*?\)/g, '').trim();
      await navigator.clipboard.writeText(textToCopy || content);
      toast.success("Message copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy:", error);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = content;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success("Message copied to clipboard!");
      } catch (err) {
        toast.error("Failed to copy message");
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDownloadProject = async (content) => {
    try {
      const projectName = extractProjectName(content);
      await downloadProjectAsZip(content, projectName);
      toast.success("Project downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error(error.message || "Failed to download project");
    }
  };

  const handleEditMessage = (message, index) => {
    setEditingMessage(index);
    setEditText(message.content);
  };

  const handleSaveEdit = async () => {
    if (editText.trim() && editingMessage !== null && editingMessage < messages.length) {
      const messageToEdit = messages[editingMessage];
      if (messageToEdit && messageToEdit.role === "user" && editMessage) {
        await editMessage(editingMessage, editText);
        toast.success("Message updated and resending...");
      }
    }
    setEditingMessage(null);
    setEditText("");
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditText("");
  };

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        minHeight: 0,
        px: { xs: 1, sm: 2, md: 3 },
        py: 1,
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {messages.length === 0 ? (
        <EmptyChatState onNewChat={() => startNewChat()} />
      ) : (
        <Stack spacing={1.5}>
          {messages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              onMouseEnter={() => setHoveredMessage(idx)}
              onMouseLeave={() => setHoveredMessage(null)}
            >
              <Stack
                direction={msg.role === "user" ? "row-reverse" : "row"}
                alignItems="flex-start"
                spacing={1.5}
              >
                {/* Avatar */}
                <Avatar
                  sx={{
                    bgcolor:
                      msg.role === "user"
                        ? "#1e3a8a"
                        : "#f1f5f9",
                    width: 36,
                    height: 36,
                    fontSize: 14,
                    color: msg.role === "user" ? "white" : "#1e3a8a",
                    fontWeight: "bold",
                    border: msg.role === "assistant" ? "1px solid #e2e8f0" : "none",
                  }}
                >
                  {msg.role === "user" ? getUserInitial() : "EXI"}
                </Avatar>

                {/* Message Bubble */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    maxWidth: { xs: "85%", sm: "80%" },
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    hyphens: "auto",
                    background: msg.role === "user"
                      ? "#1e3a8a"
                      : "#f8fafc",
                    color: msg.role === "user" ? "#ffffff" : "#0f172a",
                    borderRadius: "16px",
                    borderBottomRightRadius:
                      msg.role === "user" ? "4px" : "16px",
                    borderBottomLeftRadius:
                      msg.role === "assistant" ? "4px" : "16px",
                    border: msg.role === "assistant" ? "1px solid #e2e8f0" : "none",
                    whiteSpace: "pre-wrap",
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                    position: "relative",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ flex: 1, wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", minWidth: 0 }}>
                  {/* Display uploaded files */}
                  {msg.files && msg.files.length > 0 && (
                    <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {msg.files.map((file, fileIdx) => {
                        const isImage = file.type && file.type.startsWith('image/');
                        if (isImage) {
                          const imageUrl = file.preview || file.url || (file instanceof File ? URL.createObjectURL(file) : null);
                          return imageUrl ? (
                            <Box key={fileIdx} sx={{ position: "relative" }}>
                              <img
                                src={imageUrl}
                                alt={file.name || "Uploaded image"}
                                style={{
                                  maxWidth: "300px",
                                  maxHeight: "300px",
                                  borderRadius: "8px",
                                  margin: "0.5rem 0",
                                  objectFit: "contain"
                                }}
                                onError={(e) => {
                                  console.error("Failed to load image:", imageUrl);
                                  e.target.style.display = "none";
                                }}
                              />
                            </Box>
                          ) : null;
                        } else {
                          return (
                            <Box 
                              key={fileIdx} 
                              sx={{ 
                                display: "flex", 
                                alignItems: "center", 
                                gap: 1, 
                                p: 1, 
                                bgcolor: msg.role === "user" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: msg.role === "user" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
                                mb: 0.5
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                📎 {file.name || "Attached file"}
                              </Typography>
                            </Box>
                          );
                        }
                      })}
                    </Box>
                  )}
                  {/* Display generated image URL */}
                  {msg.imageUrl && (
                    <Box sx={{ mb: 1 }}>
                      <img
                        src={msg.imageUrl}
                        alt="Generated image"
                        style={{ maxWidth: "100%", borderRadius: "8px", margin: "0.5rem 0" }}
                        onError={(e) => {
                          console.error("Failed to load image:", msg.imageUrl);
                          e.target.style.display = "none";
                        }}
                      />
                    </Box>
                  )}
                  {msg.videoUrl && (
                    <Box sx={{ mb: 1 }}>
                      <video
                        src={msg.videoUrl}
                        controls
                        style={{ maxWidth: "100%", borderRadius: "8px", margin: "0.5rem 0" }}
                      />
                    </Box>
                  )}
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeHighlight]}
                    components={{
                      p: ({ children }) => (
                        <p style={{ lineHeight: 1.5, margin: "0.5rem 0" }}>{children}</p>
                      ),
                      ul: ({ children }) => (
                        <ul style={{ margin: "0.5rem 0", paddingLeft: "1.25rem" }}>{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol style={{ margin: "0.5rem 0", paddingLeft: "1.25rem" }}>{children}</ol>
                      ),
                      li: ({ children }) => (
                        <li style={{ margin: "0.25rem 0" }}>{children}</li>
                      ),
                      h1: ({ children }) => (
                        <h1 style={{ margin: "1rem 0 0.5rem 0", fontSize: "1.5rem", fontWeight: 600 }}>{children}</h1>
                      ),
                      h2: ({ children }) => (
                        <h2 style={{ margin: "0.75rem 0 0.4rem 0", fontSize: "1.25rem", fontWeight: 600 }}>{children}</h2>
                      ),
                      h3: ({ children }) => (
                        <h3 style={{ margin: "0.5rem 0 0.25rem 0", fontSize: "1.1rem", fontWeight: 600 }}>{children}</h3>
                      ),
                      img: ({ src, alt }) => (
                        <img src={src} alt={alt} style={{ maxWidth: "100%", borderRadius: "8px", margin: "0.5rem 0" }} />
                      ),
                      video: ({ src, ...props }) => (
                        <video
                          src={src}
                          controls
                          style={{ maxWidth: "100%", borderRadius: "8px", margin: "0.5rem 0" }}
                          {...props}
                        />
                      ),
                      code: ({ node, inline, className, children, ...props }) => {
                        const match = /language-(\w+)/.exec(className || "");
                        return !inline ? (
                          <Box sx={{ position: "relative", my: 2 }}>
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                p: 0.5,
                                bgcolor: "rgba(0,0,0,0.3)",
                                color: "#aaa",
                                fontSize: "0.7rem",
                                borderBottomLeftRadius: 4,
                                zIndex: 1,
                              }}
                            >
                              {match ? match[1] : "code"}
                            </Box>
                            <pre
                              style={{
                                margin: 0,
                                padding: "1rem",
                                borderRadius: "8px",
                                overflow: "auto",
                                backgroundColor: "rgba(0,0,0,0.4)",
                              }}
                            >
                              <code className={className} {...props}>
                                {children}
                              </code>
                            </pre>
                          </Box>
                        ) : (
                          <code
                            style={{
                              backgroundColor: "rgba(0,0,0,0.2)",
                              padding: "0.2rem 0.4rem",
                              borderRadius: "4px",
                              fontFamily: "monospace",
                            }}
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      },
                    }}
                  >
                    {(() => {
                      // Ensure content is always a string - prevent [object Object] and React Error #130
                      const content = msg.content;
                      if (typeof content === "string") {
                        return content;
                      }
                      if (content === null || content === undefined) {
                        return "";
                      }
                      if (typeof content === "object") {
                        // Try to extract meaningful text from object
                        if (content.message && typeof content.message === "string") {
                          return content.message;
                        }
                        if (content.text && typeof content.text === "string") {
                          return content.text;
                        }
                        if (content.content && typeof content.content === "string") {
                          return content.content;
                        }
                        if (content.error && typeof content.error === "string") {
                          return content.error;
                        }
                        // If it's an object with structured data, format it nicely
                        if (content.topicOverview || content.explanations || content.practicalExamples || content.commonMistakes || content.bestPractices) {
                          // Format the object as a readable string
                          let formatted = "";
                          if (content.topicOverview) formatted += `## Overview\n\n${typeof content.topicOverview === "string" ? content.topicOverview : JSON.stringify(content.topicOverview, null, 2)}\n\n`;
                          if (content.explanations) formatted += `## Explanations\n\n${Array.isArray(content.explanations) ? content.explanations.map(e => typeof e === "string" ? e : JSON.stringify(e)).join("\n\n") : (typeof content.explanations === "string" ? content.explanations : JSON.stringify(content.explanations, null, 2))}\n\n`;
                          if (content.practicalExamples) formatted += `## Practical Examples\n\n${Array.isArray(content.practicalExamples) ? content.practicalExamples.map(e => typeof e === "string" ? e : JSON.stringify(e)).join("\n\n") : (typeof content.practicalExamples === "string" ? content.practicalExamples : JSON.stringify(content.practicalExamples, null, 2))}\n\n`;
                          if (content.commonMistakes) formatted += `## Common Mistakes\n\n${Array.isArray(content.commonMistakes) ? content.commonMistakes.map(e => typeof e === "string" ? e : JSON.stringify(e)).join("\n\n") : (typeof content.commonMistakes === "string" ? content.commonMistakes : JSON.stringify(content.commonMistakes, null, 2))}\n\n`;
                          if (content.bestPractices) formatted += `## Best Practices\n\n${Array.isArray(content.bestPractices) ? content.bestPractices.map(e => typeof e === "string" ? e : JSON.stringify(e)).join("\n\n") : (typeof content.bestPractices === "string" ? content.bestPractices : JSON.stringify(content.bestPractices, null, 2))}\n\n`;
                          return formatted || "";
                        }
                        // For other objects, try to stringify as JSON (formatted)
                        try {
                          return JSON.stringify(content, null, 2);
                        } catch {
                          return "";
                        }
                      }
                      return String(content || "");
                    })()}
                  </ReactMarkdown>
                    </Box>
                    {/* 3-dots menu with all actions */}
                    <MessageActions
                      message={msg}
                      onCopy={(content) => handleCopyMessage(content)}
                      onEdit={() => handleEditMessage(msg, idx)}
                      onRegenerate={msg.role === "assistant" && idx === messages.length - 1 ? regenerateLastResponse : undefined}
                      isUserMessage={msg.role === "user"}
                    />
                  </Box>
                  {/* Download Project Button for Assistant Messages with Project Structures */}
                  {msg.role === "assistant" && isProjectStructure(msg.content) && (
                    <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-start" }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadProject(msg.content)}
                        sx={{
                          background: "linear-gradient(90deg, #1d4ed8, #3b82f6)",
                          "&:hover": { background: "linear-gradient(90deg, #1e40af, #2563eb)" },
                          textTransform: "none",
                          fontSize: "0.85rem",
                        }}
                      >
                        Download Project
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Stack>
            </motion.div>
          ))}

          {/* 🧠 Processing Message Indicator */}
          {isProcessing && processingMessage && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <Avatar
                  sx={{
                    background: "linear-gradient(135deg, #0ea5e9, #06b6d4)",
                    width: 32,
                    height: 32,
                    fontSize: 14,
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  EXI
                </Avatar>
                <Paper
                  elevation={3}
                  sx={{
                    p: 1.25,
                    maxWidth: "75%",
                    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 30%, #1d4ed8 60%, #1e40af 100%)",
                    color: "white",
                    borderRadius: 3,
                    borderBottomLeftRadius: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={16} sx={{ color: "#bfdbfe" }} />
                  <Typography sx={{ fontSize: "0.9rem", fontStyle: "italic" }}>
                    {processingMessage}
                  </Typography>
                </Paper>
              </Stack>
            </motion.div>
          )}
        </Stack>
      )}
      <div ref={chatEndRef} />

      {/* Edit Message Dialog */}
      <Dialog
        open={editingMessage !== null}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(135deg, #0c4a6e, #075985)",
            color: "white",
            border: "1px solid rgba(59, 130, 246, 0.3)",
          },
        }}
      >
        <DialogTitle>Edit Message</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={6}
            fullWidth
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                color: "white",
                bgcolor: "rgba(12, 74, 110, 0.3)",
                "& fieldset": {
                  borderColor: "rgba(59, 130, 246, 0.5)",
                },
                "&:hover fieldset": {
                  borderColor: "#60a5fa",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#3b82f6",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} sx={{ color: "#9ca3af" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            sx={{
              background: "linear-gradient(90deg, #1d4ed8, #3b82f6)",
              "&:hover": { background: "linear-gradient(90deg, #1e40af, #2563eb)" },
            }}
          >
            Save & Resend
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
